/**
 * ── O PLAYER LEMBRA O QUE O ALUNO ESCOLHEU, DE UMA AULA PARA A OUTRA ────────
 *
 * Ordem do dono (23/09/2026):
 *
 * > *«o player deve lembrar as configurações escolhidas pelo usuário entre
 * > vídeos: se ele escolheu ficar sem legenda em um vídeo, no próximo o vídeo
 * > deve ficar sem legenda. Se optou por determinada velocidade ou volume, isso
 * > se mantém entre aulas.»*
 *
 * ## Por que isto mora no ALUNO e não no player
 *
 * O `packages/snippet/src/player.ts` é o mesmo runtime que roda na página de
 * vendas de um cliente, e ele tem **teto de bytes**: medido em 23/09/2026, o
 * `dist/player.js` estava em **38,83 KB contra um limite de 38,85** — vinte
 * bytes de folga. Guardar preferência lá dentro custaria mais que isso, e o
 * custo cairia em toda VSL do produto, onde ninguém assiste a uma «próxima
 * aula». A preferência é da SALA; o lugar dela é a sala.
 *
 * ## Quatro coisas, e só quatro
 *
 * Volume (com o mudo junto, que é um volume pela mão do aluno), velocidade,
 * legenda e **o idioma do áudio**. É o que a barra do player oferece e o que
 * muda de vídeo para vídeo.
 *
 * O áudio entrou em 21/10/2026, por ordem do dono:
 *
 * > *«se ele escolhe um idioma de legenda e audio em uma aula, ela deve ser
 * > mantida nas demais mesmo em cursos diferentes»*
 *
 * Ele é o mais simples dos quatro, e de propósito: **não existe «áudio
 * desligado»**. A legenda precisa de uma união (`desligada` é escolha, e tem
 * de ser distinguível de «nunca escolheu») porque «sem legenda» é um estado
 * que o aluno pede. Um vídeo sem áudio não é um estado que alguém peça, então
 * aqui basta o código do idioma — ou `null`.
 *
 * ## `localStorage`, e o que isso significa de verdade
 *
 * A escolha é **do aparelho**, não da conta: quem assiste no computador de
 * manhã e no celular à noite escolhe duas vezes, e é o certo — o volume do
 * fone não é o volume da caixa de som. Por isso ela não vai ao servidor. E
 * toda leitura e escrita é embrulhada: em aba anónima, com dados de site
 * bloqueados, o acessor LANÇA em vez de devolver vazio, e uma aula que não
 * abre por causa de uma preferência seria a troca mais burra possível.
 *
 * A chave é única para a área inteira (e não por aula): o objetivo é
 * justamente atravessar aulas.
 */

/** A legenda escolhida: o idioma da faixa, ou `desligada`. `null` = nunca escolheu. */
export type LegendaEscolhida = { tipo: 'desligada' } | { tipo: 'idioma'; lang: string };

export interface PreferenciasDoPlayer {
  /** 0 a 1. `null` = nunca mexeu, e o player fica com o volume dele. */
  volume: number | null;
  mudo: boolean | null;
  /** 0,25 a 4. `null` = nunca mexeu. */
  velocidade: number | null;
  legenda: LegendaEscolhida | null;
  /**
   * O código do idioma do áudio que o aluno escolheu — `es-ES`, `en-US`, ou o
   * da própria faixa original quando ele volta para ela. `null` = nunca
   * escolheu, e a original manda (`T11`).
   *
   * Guardar o idioma da original quando ele volta para ela não é desperdício:
   * é o que faz a volta ser lembrada. E não cria problema numa aula de outro
   * curso gravada noutra língua — o idioma guardado não casa com nada ali, e
   * o runtime fica na original daquela aula, que é o certo.
   */
  audio: string | null;
}

export const SEM_PREFERENCIA: PreferenciasDoPlayer = {
  volume: null,
  mudo: null,
  velocidade: null,
  legenda: null,
  audio: null,
};

const CHAVE = 'vst.aluno.player';

/** Os limites do que se aceita de volta do armazenamento — e do que se grava. */
const VELOCIDADE_MINIMA = 0.25;
const VELOCIDADE_MAXIMA = 4;

/**
 * Lê o que estiver guardado, **campo a campo**.
 *
 * Um valor estragado não derruba os outros: é a lição de `salvar-o-que-vale.ts`
 * — *«perder o elemento quebrado é ruim; perder o resto junto é inexplicável»*.
 * Aqui ela é barata, porque são cinco campos e nenhum depende do outro.
 */
export function lerPreferenciasDoPlayer(cru: string | null): PreferenciasDoPlayer {
  if (!cru) return SEM_PREFERENCIA;
  let bruto: unknown;
  try {
    bruto = JSON.parse(cru);
  } catch {
    return SEM_PREFERENCIA;
  }
  if (!bruto || typeof bruto !== 'object') return SEM_PREFERENCIA;
  const o = bruto as Record<string, unknown>;

  const volume =
    typeof o.volume === 'number' && Number.isFinite(o.volume) && o.volume >= 0 && o.volume <= 1
      ? o.volume
      : null;
  const mudo = typeof o.mudo === 'boolean' ? o.mudo : null;
  const velocidade =
    typeof o.velocidade === 'number' &&
    Number.isFinite(o.velocidade) &&
    o.velocidade >= VELOCIDADE_MINIMA &&
    o.velocidade <= VELOCIDADE_MAXIMA
      ? o.velocidade
      : null;

  let legenda: LegendaEscolhida | null = null;
  const l = o.legenda as Record<string, unknown> | null | undefined;
  if (l && typeof l === 'object') {
    if (l.tipo === 'desligada') legenda = { tipo: 'desligada' };
    else if (l.tipo === 'idioma' && typeof l.lang === 'string' && l.lang.trim())
      legenda = { tipo: 'idioma', lang: l.lang.trim() };
  }

  /* Cadeia vazia vira `null`: ela casaria por prefixo com qualquer faixa lá no
     runtime, e «qualquer uma» não foi o que o aluno escolheu. */
  const audio = typeof o.audio === 'string' && o.audio.trim() ? o.audio.trim() : null;

  return { volume, mudo, velocidade, legenda, audio };
}

/** O que vai para o armazenamento: o que estava lá, com o que acabou de mudar por cima. */
export function comAMudanca(
  antes: PreferenciasDoPlayer,
  mudanca: Partial<PreferenciasDoPlayer>,
): PreferenciasDoPlayer {
  return { ...antes, ...mudanca };
}

/**
 * A legenda que o `<video>` está mostrando agora, na forma que se guarda.
 *
 * `desligada` quando nenhuma faixa está `showing` — e isso é uma ESCOLHA, não
 * uma ausência: é exatamente o caso que o dono nomeou («escolheu ficar sem
 * legenda»). Faixa sem idioma cai em `desligada` em vez de gravar `''`, que
 * não casaria com faixa nenhuma na aula seguinte.
 */
export function legendaVisivel(faixas: readonly TextTrack[]): LegendaEscolhida {
  for (const f of faixas) {
    if (f.mode !== 'showing') continue;
    const lang = (f.language || '').trim();
    return lang ? { tipo: 'idioma', lang } : { tipo: 'desligada' };
  }
  return { tipo: 'desligada' };
}

/**
 * A escolha do aluno na forma que o RUNTIME entende — `cfg.legendaEscolhida`.
 *
 * ## Por que a tradução mora aqui, e não no componente
 *
 * Porque é regra, e regra desta casa se mede sem navegador. O componente só
 * cabla: ele lê o que está guardado, chama isto, e junta o resultado à
 * configuração que o servidor mandou.
 *
 * ## Por que não há mais um «qual faixa ligar»
 *
 * Havia, e ela não servia: aplicar a escolha ao DOM depois de montar não
 * alcança nada, porque o runtime busca a faixa SÓ quando a legenda acende — na
 * aula seguinte não existe `<track>` para desligar, e no primeiro `play` ele
 * religa a que o tutor marcou como padrão. Quem decide a legenda inicial é o
 * runtime; então a escolha vai como CONFIGURAÇÃO, e é ele que a faz vencer a
 * padrão (`packages/snippet/src/player.ts`, `ligarLegendas`).
 *
 * `undefined` = nunca escolheu, e a padrão do tutor manda, como sempre. Essa
 * distinção é a coisa que este recurso inteiro não pode errar: colapsar «não
 * decidiu» em «desligada» tiraria a legenda de quem nunca a tocou — inclusive
 * do aluno surdo para quem o tutor a ligou.
 *
 * O casamento por prefixo de idioma (`pt` acha `pt-BR`, e ao contrário) e o que
 * fazer quando ESTA aula não tem o idioma guardado são decisões do runtime, e
 * estão escritas lá: idioma ausente não desliga nada, cai na padrão do tutor.
 */
export function legendaParaConfiguracao(escolha: LegendaEscolhida | null): string | undefined {
  if (!escolha) return undefined;
  if (escolha.tipo === 'desligada') return 'off';
  const lang = escolha.lang.trim();
  /* Cadeia vazia não é idioma: casaria com qualquer faixa por prefixo lá no
     runtime, e o que o aluno escolheu não foi «qualquer uma». */
  return lang ? lang : undefined;
}

/**
 * O idioma do áudio na forma que o RUNTIME entende — `cfg.audioEscolhido`.
 *
 * Mesma lei da legenda, e pelo mesmo motivo: `undefined` é «nunca escolheu», e
 * é o que deixa a original mandar. A diferença é que aqui não há um `'off'` a
 * traduzir, porque não há áudio desligado.
 *
 * O casamento por prefixo (`pt` acha `pt-BR`) e o que fazer quando ESTA aula
 * não tem a língua guardada são decisões do runtime, e estão escritas lá: a
 * aula fica na original, calada.
 */
export function audioParaConfiguracao(escolha: string | null): string | undefined {
  const lang = (escolha ?? '').trim();
  return lang ? lang : undefined;
}

/**
 * ── O ALUNO TROCOU A LÍNGUA DA ÁREA: O QUE ACONTECE AO PLAYER ──────────────
 *
 * Ordem do dono (22/10/2026): *«apesar de selecionado pelo nativo do aluno,
 * ele pode mudar para outra, e assim a preferencia do player fica por sua
 * conveniencia. **se ele altera, essa é que vale**»*.
 *
 * ## O caso que esta função existe para resolver
 *
 * A semente do servidor perde para a escolha guardada (`escolha ?? semente`,
 * em `comOAudioEscolhido`), e tem de perder: é o que faz a escolha do aluno
 * valer. Mas isso abre um buraco de uma ordem só:
 *
 * 1. o aluno troca a faixa de uma aula para inglês — fica `audio: 'en-US'`;
 * 2. depois vai ao seletor da área e escolhe **espanhol**;
 * 3. a semente passa a ser `es-ES`, perde para o `en-US` guardado, e ele
 *    continua a ouvir inglês — **logo a seguir a ter pedido espanhol**.
 *
 * As duas são alterações dele. O que decide entre elas é **qual foi a
 * última**, e a última foi a da área. Por isso trocar a língua da área
 * devolve o áudio ao estado «nunca escolheu», e a semente nova passa a mandar.
 *
 * ## A legenda DESLIGADA sobrevive, e a legenda numa língua não
 *
 * São duas coisas diferentes guardadas no mesmo campo:
 *
 * - `{ tipo: 'idioma' }` é **uma língua**, e a troca alcança-a pela mesma
 *   razão do áudio;
 * - `{ tipo: 'desligada' }` é **um modo** — «não quero legenda» —, e isso não
 *   é uma opinião sobre que língua falar. Religá-la aqui tiraria do aluno uma
 *   escolha que ele não desfez, e seria a mesma falha que o cabeçalho deste
 *   ficheiro já nomeia: colapsar «não decidiu» em «desligada» (e, aqui, o
 *   contrário).
 *
 * Volume, mudo e velocidade não são tocados: não têm língua.
 *
 * Pura, e por isso medível sem navegador — o componente só cabla.
 */
export function aoTrocarALinguaDaArea(
  atuais: PreferenciasDoPlayer,
): Partial<PreferenciasDoPlayer> {
  return {
    audio: null,
    legenda: atuais.legenda?.tipo === 'desligada' ? atuais.legenda : null,
  };
}

/* ── As duas portas do armazenamento, embrulhadas ─────────────────────────── */

export function preferenciasGuardadas(): PreferenciasDoPlayer {
  try {
    return lerPreferenciasDoPlayer(window.localStorage.getItem(CHAVE));
  } catch {
    return SEM_PREFERENCIA;
  }
}

export function guardarPreferencia(mudanca: Partial<PreferenciasDoPlayer>): void {
  try {
    const proxima = comAMudanca(preferenciasGuardadas(), mudanca);
    window.localStorage.setItem(CHAVE, JSON.stringify(proxima));
  } catch {
    /* Aba anónima, dados de site bloqueados, cota cheia: a aula continua. */
  }
}
